import { STEP_IDS } from './how-steps';

import type { ReactElement } from 'react';

/**
 * The three step buttons, and the note under them.
 *
 * Split out of `HowItWorks` at the seam it already had: the band is a step
 * LIST beside a STAGE, and holding both put the file 33 lines over the
 * ~300-line ceiling `.claude/rules/architecture.md` sets. The list is the leaf
 * of the two — it needs the current index, a setter and the translator, and
 * nothing the stage owns — so it is the half that moves.
 *
 * They are plain buttons rather than a tablist: a real tablist owes arrow-key
 * navigation and a roving tabindex, and the stage opposite is a region rather
 * than a tabpanel. See the comment at the call site for why.
 */
export function StepList({
  step,
  choose,
  t,
}: {
  /** Index of the step currently on the stage. */
  readonly step: number;
  /** Select a step. Stops the auto-advance permanently at the call site. */
  readonly choose: (i: number) => void;
  /** The active translator, passed rather than re-hooked so both halves of the
   *  band resolve their copy through one context read. */
  readonly t: (key: string) => string;
}): ReactElement {
  return (
    <div className="flex flex-col gap-2.5 self-stretch">
      {STEP_IDS.map((id, i) => {
        const on = i === step;
        return (
          <button
            key={id}
            type="button"
            onClick={() => {
              choose(i);
            }}
            aria-current={on ? 'step' : undefined}
            className={[
              // `transition-all` animated the focus OUTLINE in over 300ms along with
              // everything else — a focus indicator that fades is sluggish, and
              // measurably so: read immediately after Tab the outline is still
              // 0px wide. Scoped to the properties the selected/unselected swap
              // actually needs.
              'rounded-[15px] border p-[17px_19px] text-start transition-[background-color,border-color,box-shadow] duration-300',
              // An OUTLINE rather than a ring, and not a style choice.
              // Tailwind's `ring-*` composes through `box-shadow`, and the
              // selected branch below sets `box-shadow` directly with an
              // arbitrary `shadow-[...]` — which wins, leaving the ring
              // layers in the computed value as `rgba(0,0,0,0) 0 0 0 0`.
              // Measured: the SELECTED step showed no visible change at all
              // when focused, on a dark band, with `outline-width: 0px`.
              // The unselected steps were fine, because they carry no
              // shadow utility — so the defect was on exactly one element
              // at a time and moved as the section auto-advanced.
              //
              // `outline` is a separate property from `box-shadow`, so it
              // cannot be clobbered by one. Blue rather than cyan now this
              // band is light — `--bd-cyan` measures 1.80:1 on white, so it
              // is a FILL there and never an indicator.
              'focus-visible:[outline:2px_solid_var(--bd-blue)] focus-visible:[outline-offset:2px]',
              on
                ? // `--bd-tint` is the documented "you are here" ground — the
                  // same brand-tinted fill an active nav row uses — over a
                  // brand-tinted lift, so the selected step reads as raised
                  // rather than merely differently coloured. The border is
                  // derived from `--bd-blue-rgb` rather than a new literal;
                  // that companion exists so a consumer can pick its own
                  // alpha (see `bedrock.css`).
                  'border-[color:rgb(var(--bd-blue-rgb)/0.30)] bg-[color:var(--bd-tint)] shadow-[0_1px_2px_rgba(12,13,16,.05),0_14px_32px_-16px_rgba(35,72,219,.34)]'
                : // White over the page rather than `--bd-sunk`: a #fafafa
                  // fill on a #ffffff ground is a 2% step, which is not an
                  // edge anyone can see. The card lifts instead.
                  'border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] shadow-[0_1px_2px_rgba(12,13,16,.05),0_6px_16px_-10px_rgba(12,13,16,.16)] hover:border-[color:var(--bd-dot)] hover:shadow-[0_1px_2px_rgba(12,13,16,.06),0_10px_24px_-12px_rgba(12,13,16,.22)]',
            ].join(' ')}
          >
            <span className="flex items-center gap-2.5">
              <span
                className={[
                  'grid size-7 flex-none place-items-center rounded-[9px] font-mono text-[11.5px] font-medium',
                  on
                    ? 'text-[color:var(--bd-page)]'
                    : 'bg-[color:var(--bd-line)] text-[color:var(--bd-muted)]',
                ].join(' ')}
                style={on ? { background: 'var(--bd-gradient-ink)' } : undefined}
              >
                0{i + 1}
              </span>
              <span
                className={[
                  'min-w-0 flex-1 text-[16.5px] font-semibold tracking-[-0.028em]',
                  on ? 'text-[color:var(--bd-ink)]' : 'text-[color:var(--bd-muted)]',
                ].join(' ')}
              >
                {t(`home.how.${id}.title`)}
              </span>
              <span
                className={[
                  'flex-none whitespace-nowrap rounded-full border px-2.5 py-1 font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.12em] rtl:normal-case rtl:tracking-normal',
                  i === 0
                    ? 'border-[color:var(--bd-ink)] bg-[color:var(--bd-ink)] text-[color:var(--bd-page)]'
                    : 'border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)] text-[color:var(--bd-muted)]',
                ].join(' ')}
              >
                {t(i === 0 ? 'home.how.whoYours' : 'home.how.whoOurs')}
              </span>
            </span>
            <span
              className={[
                'mt-2.5 block text-pretty text-sm leading-[1.58] ms-[38px]',
                on ? 'text-[color:var(--bd-body)]' : 'text-[color:var(--bd-muted)]',
              ].join(' ')}
            >
              {t(`home.how.${id}.body`)}
            </span>
          </button>
        );
      })}

      {/* Deliberately the ONE surface here that stays flat: it is a
                footnote, not a step, and giving it the same lift would put it in
                the same reading order as the three things above it. */}
      {/* `mt-auto` drops this to the foot of the column so the two sides
                end on one line. It is a footnote about steps 2 and 3, so the
                bottom is where it belongs even when there is no gap to absorb —
                and when the columns stack on a narrow viewport there is no free
                space and this is inert. */}
      <p className="mt-auto flex items-start gap-2.5 rounded-[15px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-sunk)] px-[18px] py-3.5">
        <span
          aria-hidden="true"
          data-bd-anim
          className="mt-1.5 size-[7px] flex-none rounded-full"
          style={{
            background: 'linear-gradient(120deg,var(--bd-cyan),var(--bd-violet))',
            animation: 'bdPulse 2.4s ease-in-out infinite',
          }}
        />
        <span className="text-[13px] leading-[1.55] text-[color:var(--bd-body)]">
          {t('home.how.note')}
        </span>
      </p>
    </div>
  );
}
